import { Auth } from 'aws-amplify';
import CookiesJs from 'js-cookie';

import {
  CookieNames,
  Cookies,
  CookiesInfo,
  UserData,
  UserInformation,
  UserRoleEnum,
} from '@/types/auth.types';

const getAccessToken = async () => {
  try {
    const session = await Auth.currentSession();
    const accessToken = session.getAccessToken().getJwtToken();
    return accessToken;
  } catch {
    //
  }
};

const extractAndParseCookies = (
  cookieCompatibility: 'amplify' | 'elasticsearch' = 'amplify',
): CookiesInfo => {
  const cookies = CookiesJs.get();
  if (!cookies) {
    return {} as CookiesInfo;
  }

  const cookieNames: CookieNames =
    cookieCompatibility === 'amplify'
      ? getAmplifyCookieNames(cookies)
      : getElasticsearchCookieNames();

  const result = {
    userData: cookies[cookieNames.userDataKey],
    tokenUserName: cookies[cookieNames.lastUserKey],
    idToken: cookies[cookieNames.idTokenKey],
    accessToken: cookies[cookieNames.accessTokenKey],
    refreshToken: cookies[cookieNames.refreshTokenKey],
  };

  return result;
};

const getElasticsearchCookieNames = () => {
  return {
    idTokenKey: 'ID-TOKEN',
    accessTokenKey: 'ACCESS-TOKEN',
    refreshTokenKey: 'REFRESH-TOKEN',
    cognitoEnabledKey: 'COGNITO-ENABLED',
  };
};

const getAmplifyCookieNames = (cookiesOrUserName: Cookies | string) => {
  const keyPrefix = `CognitoIdentityServiceProvider.${import.meta.env.VITE_USER_POOL_WEB_CLIENT_ID}`;
  const lastUserKey = `${keyPrefix}.LastAuthUser`;
  const tokenUserName =
    typeof cookiesOrUserName === 'string'
      ? cookiesOrUserName
      : cookiesOrUserName[lastUserKey];

  const result = {
    lastUserKey,
    userDataKey: `${keyPrefix}.${tokenUserName}.userData`,
    idTokenKey: `${keyPrefix}.${tokenUserName}.idToken`,
    accessTokenKey: `${keyPrefix}.${tokenUserName}.accessToken`,
    refreshTokenKey: `${keyPrefix}.${tokenUserName}.refreshToken`,
  };

  return result;
};

const getUserInformation = (): UserInformation => {
  const cookiesData = extractAndParseCookies();
  const userData: UserData = JSON.parse(cookiesData?.userData ?? '{}');
  const role = userData?.UserAttributes?.find(
    attribute => attribute?.Name === 'custom:admin_type',
  )?.Value;
  const organizationId = userData?.UserAttributes?.find(
    attribute => attribute?.Name === 'custom:mst_organization_id',
  )?.Value;
  const name = userData?.UserAttributes?.find(
    attribute => attribute?.Name === 'name',
  )?.Value;
  const saleAreaId = userData?.UserAttributes?.find(
    attribute => attribute?.Name === 'custom:sale_area_id',
  )?.Value;

  return {
    role,
    name,
    saleAreaId,
    organizationId,
    userName: userData?.Username,
  };
};

const getPathnameFromUrl = (url: string, includeSearch = false) => {
  const pathname = url.split(
    import.meta.env.DEV
      ? 'http://localhost:5173'
      : import.meta.env.VITE_APP_URL,
  )?.[1];

  if (includeSearch) return pathname;
  return pathname?.replace(/\?.*/gm, '');
};

const checkIsChainDomain = () => {
  const appUrl = import.meta.env.VITE_APP_URL;
  const appUrlChain = import.meta.env.VITE_APP_URL_CHAIN;
  return appUrl === appUrlChain;
};

const checkCanAccessByRole = () => {
  if (import.meta.env.DEV) return true;
  const { role } = getUserInformation();
  const isChainDomain = checkIsChainDomain();
  const isChainOrWeddingRole = [
    UserRoleEnum.organization,
    UserRoleEnum.wedding,
  ].includes(role!);

  return !(
    (isChainOrWeddingRole && !isChainDomain) ||
    (!isChainOrWeddingRole && isChainDomain)
  );
};

export {
  checkCanAccessByRole,
  checkIsChainDomain,
  extractAndParseCookies,
  getAccessToken,
  getPathnameFromUrl,
  getUserInformation,
};
